import { ReactNode, useCallback, useRef, useState } from 'react';

import useDebounce from '@/data/hook/useDebounce';

import { SearchSelectValue } from './types';

const SEARCH_DEBOUNCE_TIME_IN_MS = 500;

type UseSearchSelectParams = {
  value?: SearchSelectValue;
  onChange: (value: SearchSelectValue) => void;
  onSearch: (search: string) => void;
  disabled?: boolean;
  selectedLabel?: string;
};

export const useSearchSelect = ({
  value,
  onChange,
  onSearch,
  disabled,
  selectedLabel,
}: UseSearchSelectParams) => {
  const optionsRegistry = useRef(new Map<string, ReactNode>());
  const [, setVersion] = useState(0);

  const [searchValue, setSearchValue] = useState('');
  const { debounce, cancel } = useDebounce(SEARCH_DEBOUNCE_TIME_IN_MS);

  const selectedKey = value != null ? String(value) : undefined;

  const registeredLabel = selectedKey
    ? optionsRegistry.current.get(selectedKey)
    : undefined;

  const selectedLabelText =
    (typeof registeredLabel === 'string' ? registeredLabel : undefined) ??
    selectedLabel ??
    '';

  const registerOption = useCallback(
    (optionValue: string, label: ReactNode) => {
      optionsRegistry.current.set(optionValue, label);
      if (optionValue === selectedKey) setVersion(v => v + 1);
    },
    [selectedKey],
  );

  const unregisterOption = useCallback(
    (optionValue: string) => {
      optionsRegistry.current.delete(optionValue);
      if (optionValue === selectedKey) setVersion(v => v + 1);
    },
    [selectedKey],
  );

  const handleSearchChange = (text: string) => {
    setSearchValue(text);
    debounce(() => onSearch(text));
  };

  const resetSearch = () => {
    setSearchValue('');
    cancel();
    onSearch('');
  };

  const handleSelect = useCallback(
    (optionValue: SearchSelectValue) => {
      if (disabled) return;
      onChange(optionValue);
      const htmlActiveElement = document.activeElement as HTMLElement;
      htmlActiveElement?.blur();
    },
    [disabled, onChange],
  );

  return {
    searchValue,
    selectedLabelText,
    handleSearchChange,
    handleSelect,
    resetSearch,
    registerOption,
    unregisterOption,
  };
};
